import { U8 } from '../engine/typed-arrays.js';
import { TU } from '../engine/gpu-flags.js';
import { getPbrGroupBuilder } from '../material/pbr/pbr-material.js';
import { mipLevelCount } from '../texture/mip-count.js';
import { linearToSrgbByte } from '../math/color.js';

const identityTexWrap = (tex) => tex;
function uploadTex(engine, bitmap, srgb, sampler, generateMipmaps, fallback) {
  const device = engine._device;
  const w = bitmap?.width ?? 1;
  const h = bitmap?.height ?? 1;
  const fmt = srgb ? "rgba8unorm-srgb" : "rgba8unorm";
  const mips = bitmap ? mipLevelCount(w, h) : 1;
  const tex = device.createTexture({
    size: { width: w, height: h },
    format: fmt,
    usage: TU.TEXTURE_BINDING | TU.COPY_DST | TU.COPY_SRC | TU.RENDER_ATTACHMENT,
    mipLevelCount: mips
  });
  if (bitmap) {
    device.queue.copyExternalImageToTexture({ source: bitmap }, { texture: tex, premultipliedAlpha: false }, { width: w, height: h });
    generateMipmaps(engine, tex);
  } else {
    device.queue.writeTexture({ texture: tex }, fallback ?? new U8([255, 255, 255, 255]), { bytesPerRow: 4 }, { width: 1, height: 1 });
  }
  const result = {
    texture: tex,
    view: tex.createView(),
    sampler,
    width: w,
    height: h
  };
  engine._dlr?.b(result, bitmap, srgb, !!bitmap, fallback);
  return result;
}
function uploadBaseColorFactorTexture(engine, factor, sampler, generateMipmaps) {
  return uploadTex(
    engine,
    null,
    true,
    sampler,
    generateMipmaps,
    new U8([linearToSrgbByte(factor[0]), linearToSrgbByte(factor[1]), linearToSrgbByte(factor[2]), Math.round(Math.max(0, Math.min(1, factor[3])) * 255)])
  );
}
function uploadOrmFactorTexture(engine, roughness, metallic, sampler, generateMipmaps) {
  const clamp = (value) => Math.round(Math.max(0, Math.min(1, value)) * 255);
  return uploadTex(engine, null, false, sampler, generateMipmaps, new U8([255, clamp(roughness), clamp(metallic), 255]));
}
function needsGltfEmissive(mat, emissiveTexture) {
  const ef = mat._emissiveFactor;
  return !(ef[0] === 0 && ef[1] === 0 && ef[2] === 0 || !!emissiveTexture && ef[0] === 1 && ef[1] === 1 && ef[2] === 1);
}
async function applyGltfOptInPbrFeatures(props, mat) {
  if (!props._emissiveColor && needsGltfEmissive(mat, props.emissiveTexture)) {
    const { setPbrEmissive } = await import('../material/pbr/set-emissive.js');
    const ef = mat._emissiveFactor;
    setPbrEmissive(props, [ef[0], ef[1], ef[2]]);
  }
  if (mat._alphaMode === "MASK") {
    const { setPbrAlphaCutoff } = await import('../material/pbr/set-alpha-cutoff.js');
    setPbrAlphaCutoff(props, mat._alphaCutoff);
  }
}
function assemblePbrProps(mat, baseColorTexture, ormTexture, normalTexture, emissiveTexture, extLayers) {
  const props = {
    baseColorTexture,
    normalTexture,
    ormTexture,
    emissiveTexture,
    ...mat._baseColorImage && !isDefaultBaseColorFactor(mat._baseColorFactor) ? { baseColorFactor: mat._baseColorFactor } : void 0,
    doubleSided: mat._doubleSided,
    occlusionStrength: mat._occlusionImage ? 1 : 0,
    ...mat._normalScale !== 1 ? { normalTextureScale: mat._normalScale } : void 0,
    ...mat._metallicRoughnessImage ? { metallicFactor: mat._metallicFactor, roughnessFactor: mat._roughnessFactor } : void 0,
    enableSpecularAA: true,
    ...mat._alphaMode === "BLEND" ? { alphaBlend: true, alpha: mat._baseColorFactor[3] } : void 0,
    ...mat._alphaMode === "MASK" ? { alpha: mat._baseColorFactor[3] } : void 0,
    ...mat._rawMatDef?.name ? { name: mat._rawMatDef.name } : void 0,
    ...extLayers,
    _buildGroup: getPbrGroupBuilder(),
    _uboVersion: 0
  };
  return props;
}
function isDefaultBaseColorFactor(f) {
  return f[0] === 1 && f[1] === 1 && f[2] === 1 && f[3] === 1;
}
function buildDefaultPbrTextures(engine, mat, sampler, generateMipmaps, getCachedTex) {
  const baseColorTexture = mat._baseColorImage ? getCachedTex(mat._baseColorImage, true) : uploadBaseColorFactorTexture(engine, mat._baseColorFactor, sampler, generateMipmaps);
  const normalTexture = mat._normalImage ? getCachedTex(mat._normalImage, false) : void 0;
  const emissiveTexture = mat._emissiveImage ? getCachedTex(mat._emissiveImage, true) : void 0;
  const single = mat._metallicRoughnessImage ?? mat._occlusionImage;
  let ormTexture;
  if (single && (!mat._metallicRoughnessImage || !mat._occlusionImage || mat._metallicRoughnessImage === mat._occlusionImage)) {
    ormTexture = getCachedTex(single, false);
  } else if (!single) {
    ormTexture = uploadOrmFactorTexture(engine, mat._roughnessFactor, mat._metallicFactor, sampler, generateMipmaps);
  } else {
    ormTexture = getCachedTex(mat._metallicRoughnessImage, false);
  }
  return { baseColorTexture, ormTexture, normalTexture, emissiveTexture };
}

export { applyGltfOptInPbrFeatures, assemblePbrProps, buildDefaultPbrTextures, identityTexWrap, needsGltfEmissive, uploadBaseColorFactorTexture, uploadOrmFactorTexture, uploadTex };
//# sourceMappingURL=gltf-pbr-builder.js.map
